import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import { loginSchema } from '@eriko/validation';
import { useLogin } from '../../hooks/useAuth';

export function LoginPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const login = useLogin();

  // Redirect if already logged in (check localStorage directly — store may not be hydrated yet)
  if (localStorage.getItem('eriko_access_token')) return <Navigate to="/" replace />;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string;
        fieldErrors[field] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    login.mutate(result.data);
  }

  return (
    <div className="flex min-h-screen bg-surface-50">
      {/* Left — Brand panel */}
      <div className="hidden lg:flex lg:w-[480px] bg-brand-600 flex-col justify-between p-12">
        <div className="flex items-center">
          <img src="/assets/logo.png" alt="Eriko" className="h-8 w-auto brightness-0 invert" />
        </div>

        <div>
          <h1 className="text-3xl font-bold text-white leading-tight tracking-tight whitespace-pre-wrap">
            {t('login.tagline')}
          </h1>
          <p className="mt-4 text-brand-100 text-[15px] leading-relaxed max-w-sm">
            {t('login.description')}
          </p>
        </div>

        <p className="text-brand-200 text-xs">{t('copyright', { ns: 'common' })}</p>
      </div>

      {/* Right — Login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center mb-10">
            <img src="/assets/logo.png" alt="Eriko" className="h-8 w-auto" />
          </div>

          <h2 className="text-2xl font-bold text-surface-900 tracking-tight">{t('login.title')}</h2>
          <p className="mt-1.5 text-sm text-surface-500">
            {t('login.subtitle')}
          </p>

          {/* Social buttons (disabled) */}
          <div className="mt-8 flex gap-3">
            <button
              disabled
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-surface-200 bg-surface-50 text-surface-400 text-sm font-medium cursor-not-allowed opacity-60"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </button>
            <button
              disabled
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-surface-200 bg-surface-50 text-surface-400 text-sm font-medium cursor-not-allowed opacity-60"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Facebook
            </button>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-surface-200" />
            <span className="text-xs text-surface-400 uppercase tracking-wider font-medium">{t('login.or')}</span>
            <div className="flex-1 h-px bg-surface-200" />
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-surface-700">
                {t('login.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1.5"
                autoComplete="email"
                autoFocus
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-surface-700">
                  {t('login.password')}
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('login.passwordPlaceholder')}
                  className="pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" strokeWidth={1.8} />
                  ) : (
                    <Eye className="w-4 h-4" strokeWidth={1.8} />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
              )}
            </div>

            {login.error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2.5">
                <p className="text-sm text-red-700">
                  {(login.error as Error).message === 'INVALID_CREDENTIALS'
                    ? t('login.invalidCredentials')
                    : (login.error as Error).message}
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-10 text-sm font-semibold"
              disabled={login.isPending}
            >
              {login.isPending ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-surface-500">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="font-semibold text-brand-600 hover:text-brand-700">
              {t('login.signUp')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
